/**
 * URS-F486 useStatuses
 *
 * Hook para consumir la config de estados persistida en BD
 * (`/api/config/study-statuses` y `/api/config/appointment-statuses`).
 *
 * Uso:
 *   const { statuses, byKey, defaultKey, loading } = useStatuses('study');
 *   const { statuses, byKey, defaultKey, loading } = useStatuses('appointment');
 *
 * Cache en modulo (sin invalidacion sofisticada — se refresca al recargar
 * pagina). Suficiente porque solo el admin toca esto y raramente.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cache = {}; // {endpoint: {statuses, byKey, defaultKey}}

const endpointFor = (entity) => entity === 'study' ? 'study-statuses' : 'appointment-statuses';

async function fetchStatuses(entity) {
  const endpoint = endpointFor(entity);
  const r = await axios.get(`${API_URL}/api/config/${endpoint}`, { withCredentials: true });
  const list = (r.data || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const byKey = Object.fromEntries(list.map(s => [s.key, s]));
  const defaultKey = (list.find(s => s.is_default) || list[0])?.key || (entity === 'study' ? 'activo' : 'citada');
  const payload = { statuses: list, byKey, defaultKey };
  cache[endpoint] = payload;
  return payload;
}

export function refreshStatusesCache(entity) {
  const endpoint = endpointFor(entity);
  delete cache[endpoint];
  return fetchStatuses(entity);
}

export function useStatuses(entity) {
  const endpoint = endpointFor(entity);
  const [state, setState] = useState(() => cache[endpoint] || { statuses: [], byKey: {}, defaultKey: entity === 'study' ? 'activo' : 'citada' });
  const [loading, setLoading] = useState(!cache[endpoint]);

  useEffect(() => {
    if (cache[endpoint]) {
      setState(cache[endpoint]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchStatuses(entity)
      .then(payload => { if (!cancelled) { setState(payload); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entity, endpoint]);

  return { ...state, loading };
}

/**
 * Utilidad para renderizar el label localizado de un status por su key.
 * Si el status no esta en la config (o aun no cargo), devuelve la key en crudo.
 */
export function statusLabel(byKey, key, lang) {
  const s = byKey?.[key];
  if (!s) return key || '';
  return lang === 'es' ? s.label_es : s.label_en;
}

/**
 * Devuelve el color hex configurado o un gris neutro por defecto.
 */
export function statusColor(byKey, key) {
  return byKey?.[key]?.color || '#5b6473';
}
